import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

export default function ServiceImageModal({
  open,
  onClose,
  initialImage,
  maxImages = 10,
  onUpload
}) {
  const { t } = useTranslation();
  const [images, setImages] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingPreviews, setPendingPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (initialImage && typeof initialImage === "string") {
      const urlArray = initialImage
        .split(",")
        .map((url) => url.trim())
        .filter(Boolean);
      setImages(urlArray);
    } else {
      setImages([]);
    }
    // Clear pending files when modal opens
    setPendingFiles([]);
    setPendingPreviews([]);
  }, [initialImage, open]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const remainingSlots = maxImages - images.length - pendingFiles.length;
    const filesToAdd = files.slice(0, remainingSlots);

    setPendingFiles(prev => [...prev, ...filesToAdd]);

    // Generate previews
    filesToAdd.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPendingPreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });

    e.target.value = "";
  };

  const handleUploadClick = async () => {
    if (pendingFiles.length === 0) return;

    setUploading(true);
    try {
      await onUpload(pendingFiles, images);
      setPendingFiles([]);
      setPendingPreviews([]);
      onClose();
    } catch (err) {
      console.error("Upload error:", err);
    }
    setUploading(false);
  };

  const handleDeleteUploaded = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    onUpload([], newImages);
  };

  const handleDeletePending = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
    setPendingPreviews(prev => prev.filter((_, i) => i !== index));
  };

  if (!open) return null;

  const totalImages = images.length + pendingFiles.length;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(4px)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        animation: "fadeIn 0.2s ease",
        padding: "12px"
      }}
      onClick={onClose}
    >
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { 
              opacity: 0;
              transform: translateY(20px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          padding: "20px",
          width: "100%",
          maxWidth: 600,
          maxHeight: "85vh",
          overflow: "auto",
          position: "relative",
          fontFamily: "Inter, Segoe UI, Arial, sans-serif",
          animation: "slideUp 0.3s ease"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            marginBottom: 20,
            fontWeight: 700,
            fontSize: 18,
            color: "#1e293b",
            textAlign: "center",
            letterSpacing: -0.3
          }}
        >
          {t("ServiceImageModal.title", "Service Images")} ({totalImages}/{maxImages})
        </div>

        {/* Uploaded Images Grid */}
        {images.length > 0 && (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#64748b", marginBottom: 12 }}>
              {t("ServiceImageModal.uploaded_images", "Uploaded Images")}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
                gap: 10,
                marginBottom: 20
              }}
            >
              {images.map((url, index) => (
                <div
                  key={`uploaded-${index}`}
                  style={{
                    position: "relative",
                    borderRadius: 10,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    border: "2px solid #e2e8f0",
                    aspectRatio: "1"
                  }}
                >
                  <img
                    src={url}
                    alt={`Service ${index + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block"
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => handleDeleteUploaded(index)}
                    style={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      width: 24,
                      height: 24,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                      fontWeight: 700,
                      boxShadow: "0 2px 6px rgba(239, 68, 68, 0.3)",
                      padding: 0
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pending Images Grid (to be uploaded) */}
        {pendingFiles.length > 0 && (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#f59e0b", marginBottom: 12 }}>
              {t("ServiceImageModal.pending_upload", "Pending Upload")} ({pendingFiles.length})
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
                gap: 10,
                marginBottom: 20
              }}
            >
              {pendingPreviews.map((preview, index) => (
                <div
                  key={`pending-${index}`}
                  style={{
                    position: "relative",
                    borderRadius: 10,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    border: "2px dashed #f59e0b",
                    aspectRatio: "1"
                  }}
                >
                  <img
                    src={preview}
                    alt={`Pending ${index + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                      opacity: 0.9
                    }}
                  />
                  <div style={{
                    position: "absolute",
                    top: 4,
                    left: 4,
                    background: "#f59e0b",
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 600,
                    padding: "3px 6px",
                    borderRadius: 4
                  }}>
                    NEW
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeletePending(index)}
                    disabled={uploading}
                    style={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      background: "rgba(239, 68, 68, 0.95)",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      width: 24,
                      height: 24,
                      cursor: uploading ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                      fontWeight: 700,
                      padding: 0,
                      opacity: uploading ? 0.5 : 1
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Select Images Button */}
        {totalImages < maxImages && (
          <label
            style={{
              display: "block",
              background: "#f8fafc",
              border: "2px dashed #cbd5e1",
              color: "#475569",
              padding: "16px 24px",
              borderRadius: 12,
              textAlign: "center",
              cursor: uploading ? "not-allowed" : "pointer",
              fontWeight: 600,
              fontSize: 15,
              transition: "all 0.2s ease",
              marginBottom: 16
            }}
            onMouseEnter={(e) => {
              if (!uploading) {
                e.currentTarget.style.background = "#f1f5f9";
                e.currentTarget.style.borderColor = "#667eea";
                e.currentTarget.style.color = "#667eea";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#f8fafc";
              e.currentTarget.style.borderColor = "#cbd5e1";
              e.currentTarget.style.color = "#475569";
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 8 }}>
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
            <div>
              {t("ServiceImageModal.select_images", "Select Images")}
            </div>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              disabled={uploading}
              style={{ display: "none" }}
            />
          </label>
        )}

        {/* Upload Button - Only show when there are pending files */}
        {pendingFiles.length > 0 && (
          <button
            type="button"
            onClick={handleUploadClick}
            disabled={uploading}
            style={{
              width: "100%",
              background: uploading
                ? "#94a3b8"
                : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              color: "#fff",
              border: "none",
              borderRadius: 12,
              padding: "16px 24px",
              fontSize: 16,
              fontWeight: 700,
              cursor: uploading ? "not-allowed" : "pointer",
              transition: "all 0.2s ease",
              marginBottom: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              boxShadow: uploading ? "none" : "0 4px 16px rgba(16, 185, 129, 0.3)"
            }}
          >
            {uploading ? (
              <>
                <div style={{
                  width: 18,
                  height: 18,
                  border: "3px solid rgba(255, 255, 255, 0.3)",
                  borderTopColor: "#fff",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite"
                }} />
                <span>{t("ServiceImageModal.uploading", "Uploading...")}</span>
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <span>{t("ServiceImageModal.upload_button", { count: pendingFiles.length })}</span>
              </>
            )}
          </button>
        )}

        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          disabled={uploading}
          style={{
            width: "100%",
            background: "#f1f5f9",
            color: "#475569",
            border: "none",
            borderRadius: 12,
            padding: "14px 24px",
            fontSize: 15,
            fontWeight: 600,
            cursor: uploading ? "not-allowed" : "pointer",
            transition: "all 0.2s ease",
            opacity: uploading ? 0.5 : 1
          }}
        >
          {t("ServiceImageModal.close", "Close")}
        </button>
      </div>
    </div>
  );
}
